import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'

import {
  IGetUserInfoSuccessResponseBody,
  IUserState,
  TUserActionTypes,
  UserActionTypes,
  ISetLastUsedAccountSuccessPayload,
  ISetLastUsedWorkspaceSuccessPayload,
  IUpdateProfileSuccessPayload,
  IUpdateProfileFailurePayload,
  IChangeAvatarSuccessPayload,
} from './types'

import {
  TAuthenticationActionTypes,
  AuthenticationActionTypes,
  ISignInSuccessPayload,
} from '@SiteModules/Authentication/types'

import {
  OnboardingActionTypes,
  TOnboardingActionTypes,
  IStartQuickstartProcessingPayload,
} from '@SiteModules/Onboarding/types'

export const initialState: IUserState = {
  user: {
    id: -1,
    attributes: {
      name: '',
      email:  '',
      lastName:  '',
      role: '',
      onboardingState: '',
      confirmedEmail: true,
      relevantAlert: null,
      shouldSeeNewsletterPopup: false,
      numberOfProjectsSuccessfullyCreated: 0,
      hasSelectedRole: false,
      firstProjectType: undefined,
    },
  },
  hasLoadedSuccessfully: false,
  saving: false,
  hasUpdatedProfileSuccessfully: false,
  useLightTheme: false,
  errorMessageCurrentPasswordForNewEmail: null,
  errorMessageCurrentPasswordForNewPassword: null,
  uploadingAvatar: false
}

type TAction = TActionTypes | TUserActionTypes | TAuthenticationActionTypes | TOnboardingActionTypes

export const userReducer = (
  state: IUserState = initialState,
  action: TAction
): IUserState => {
  return produce(state, draft => {
    switch (action.type) {
      case UserActionTypes.GET_INFO:
        loadUserInfo(draft)
        break

      case UserActionTypes.GET_INFO_FAILURE:
        loadUserInfoFailure(draft)
        break

      case UserActionTypes.GET_INFO_SUCCESS:
        loadUserInfoSuccess(draft, action)
        break

      case UserActionTypes.SET_LAST_VIEWED_ALERT:
        removeRelevantAlert(draft)
        break

      case UserActionTypes.SET_LAST_USED_ACCOUNT_SUCCESS:
        setLastUsedAccountSuccess(draft, action)
        break

      case UserActionTypes.SET_LAST_USED_WORKSPACE_SUCCESS:
        setLastUsedWorkspaceSuccess(draft, action)
        break

      case UserActionTypes.RESET_SHOULD_SEE_NEWSLETTER_POPUP_SUCCESS:
        resetShouldSeeNewsletterPopupSuccess(draft)
        break

      case UserActionTypes.SET_USE_LIGHT_THEME:
        setUseLightTheme(draft)
        break

      case UserActionTypes.RESET_USE_LIGHT_THEME:
        resetUseLightTheme(draft)
        break

      case UserActionTypes.UPDATE_PROFILE:
        updateProfile(draft)
        break

      case UserActionTypes.UPDATE_PROFILE_SUCCESS:
        updateProfileSuccess(draft, action)
        break

      case UserActionTypes.UPDATE_PROFILE_FAILURE:
        updateProfileFailure(draft, action)
        break

      case AuthenticationActionTypes.SIGN_IN_SUCCESS:
        signInSuccess(draft, action)
        break

      case OnboardingActionTypes.START_QUICKSTART_PROCESSING:
        setFirstProjectType(draft, action)
        break

      case OnboardingActionTypes.FINISH_SURVEY_SUCCESS:
        finishSurveySuccess(draft)
        break

      case UserActionTypes.SET_UPLOADING_AVATAR:
        setUploadingAvatar(draft)
        break

      case UserActionTypes.CHANGE_AVATAR_SUCCESS:
        changeAvatarSuccess(draft, action)
        break

      case UserActionTypes.CHANGE_AVATAR_FAILURE:
        changeAvatarFailure(draft)
        break

      default:
        break
    }
  })
}

const loadUserInfo = (draft: Draft<IUserState>) => {
  draft.hasLoadedSuccessfully = false
}

const loadUserInfoFailure = (draft: Draft<IUserState>) => {
  draft.hasLoadedSuccessfully = false
}

function isLoadUserInfoSuccessAction(arg: any): arg is IAction<IGetUserInfoSuccessResponseBody> {
  return arg.type === UserActionTypes.GET_INFO_SUCCESS
}

const loadUserInfoSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isLoadUserInfoSuccessAction(action)) { return }

  const userData = action.payload.user.data
  draft.user = { id: parseInt(userData.id, 10), attributes: userData.attributes }
  draft.hasLoadedSuccessfully = true
}

const removeRelevantAlert = (draft: Draft<IUserState>) => {
  draft.user.attributes.relevantAlert = null
}

function isSetLastUsedAccountSuccessAction(arg: any): arg is IAction<ISetLastUsedAccountSuccessPayload> {
  return arg.type === UserActionTypes.SET_LAST_USED_ACCOUNT_SUCCESS
}

const setLastUsedAccountSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isSetLastUsedAccountSuccessAction(action)) { return }

  draft.user.attributes.lastUsedAccountId = action.payload.accountId
}

function isSetLastUsedWorkspaceSuccessAction(arg: any): arg is IAction<ISetLastUsedWorkspaceSuccessPayload> {
  return arg.type === UserActionTypes.SET_LAST_USED_WORKSPACE_SUCCESS
}

const setLastUsedWorkspaceSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isSetLastUsedWorkspaceSuccessAction(action)) { return }

  draft.user.attributes.lastUsedWorkspaceId = action.payload.workspaceId
}

const resetShouldSeeNewsletterPopupSuccess = (draft: Draft<IUserState>) => {
  draft.user.attributes.shouldSeeNewsletterPopup = false
}

const setUseLightTheme = (draft: Draft<IUserState>) => {
  draft.useLightTheme = true
}

const resetUseLightTheme = (draft: Draft<IUserState>) => {
  draft.useLightTheme = false
}

const updateProfile = (draft: Draft<IUserState>) => {
  draft.saving = true
  draft.hasUpdatedProfileSuccessfully = false
}

function isUpdateProfileSuccessAction(arg: any): arg is IAction<IUpdateProfileSuccessPayload> {
  return arg.type === UserActionTypes.UPDATE_PROFILE_SUCCESS
}

const updateProfileSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isUpdateProfileSuccessAction(action)) { return }

  if (typeof(action.payload.lastName) === 'string') {
    draft.user.attributes.lastName = action.payload.lastName
  }

  if (typeof(action.payload.name) === 'string') {
    draft.user.attributes.name = action.payload.name
  }

  if (typeof(action.payload.email) === 'string') {
    draft.user.attributes.email = action.payload.email
  }

  draft.saving = false
  draft.hasUpdatedProfileSuccessfully = true
}

function isUpdateProfileFailureAction(arg: any): arg is IAction<IUpdateProfileFailurePayload> {
  return arg.type === UserActionTypes.UPDATE_PROFILE_FAILURE
}

const updateProfileFailure = (draft: Draft<IUserState>, action: TAction) => {
  if (!isUpdateProfileFailureAction(action)) { return }

  if (action.payload.errorMessageCurrentPasswordForNewEmail) {
    draft.errorMessageCurrentPasswordForNewEmail = action.payload.errorMessageCurrentPasswordForNewEmail
  }

  if (action.payload.errorMessageCurrentPasswordForNewPassword) {
    draft.errorMessageCurrentPasswordForNewPassword = action.payload.errorMessageCurrentPasswordForNewPassword
  }

  draft.saving = false
  draft.hasUpdatedProfileSuccessfully = false
}

function isSignInSuccessAction(arg: any): arg is IAction<ISignInSuccessPayload> {
  return arg.type === AuthenticationActionTypes.SIGN_IN_SUCCESS
}

const signInSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isSignInSuccessAction(action)) { return }

  draft.user.attributes = action.payload.attributes
}

function isStartQuickstarProcessingAction(arg: any): arg is IAction<IStartQuickstartProcessingPayload> {
  return arg.type === OnboardingActionTypes.START_QUICKSTART_PROCESSING
}

const setFirstProjectType = (draft: Draft<IUserState>, action: TAction) => {
  if (!isStartQuickstarProcessingAction(action)) { return }

  if (action.payload.repurposing) {
    draft.user.attributes.firstProjectType = 'clips'
  } else if (action.payload.dubbing) {
    draft.user.attributes.firstProjectType = 'dubbing'
  } else {
    draft.user.attributes.firstProjectType = 'subtitles'
  }
}

const finishSurveySuccess = (draft: Draft<IUserState>) => {
  draft.user.attributes.onboardingState = 'processed'
}

const setUploadingAvatar = (draft: Draft<IUserState>) => {
  draft.uploadingAvatar = true
}

function isChangeAvatarSuccessAction(arg: any): arg is IAction<IChangeAvatarSuccessPayload> {
  return arg.type === UserActionTypes.CHANGE_AVATAR_SUCCESS
}

const changeAvatarSuccess = (draft: Draft<IUserState>, action: TAction) => {
  if (!isChangeAvatarSuccessAction(action)) { return }

  draft.user.attributes.avatarUrl = action.payload.url
  draft.uploadingAvatar = false
}

const changeAvatarFailure = (draft: Draft<IUserState>) => {
  draft.uploadingAvatar = false
}
